import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Mic,
  CheckCircle,
  AlertCircle,
  Keyboard,
  Square,
  ArrowLeft,
  Loader,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import DictationHistory from './DictationHistory';
import MacAppPromotionBanner from '../../components/common/MacAppPromotionBanner';
import OutputLanguageSelect from '../../components/OutputLanguageSelect';
import { getCurrentOutputLanguage } from '../../hooks/useOutputLanguage';
import { useBatchRecorder } from '../../hooks/useBatchRecorder';
import { RootState } from '../../reducers';
import {
  closeDictationModal,
  startRecording,
  startStreamingRecording,
  stopStreamingRecording,
  processStreamingAudioDictation,
  processBatchAudioDictation,
  setDictationMode,
  resetDictation,
  DictationMode,
} from './dictationSlice';
import { config } from '../../../config';
import useLimits from '../plan/hooks/limits';

const USE_BATCH_MODE = config.dictationTranscriptionMode === 'batch';

const DictationModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const guardLimits = useLimits();
  const { isModalOpen, status, mode, transcript, error } = useSelector(
    (state: RootState) => state.dictation,
  );

  const { team } = useSelector((state: RootState) => state);
  const teamCustomVocabulary =
    team.selectedTeam?.fileOptions?.customVocabulary || '';

  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [editableTranscript, setEditableTranscript] = useState('');
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  // Batch recorder hook - onAudioReady dispatches the batch thunk
  const batchRecorder = useBatchRecorder({
    onAudioReady: useCallback(
      (audioBlob: Blob) => {
        dispatch(
          processBatchAudioDictation({
            audioBlob,
            options: {
              mode,
              customVocabulary: teamCustomVocabulary,
              languageCode: navigator.language.split('-')[0],
              // outputLanguage is captured at start time via closure
            },
          }),
        );
      },
      [dispatch, mode, teamCustomVocabulary],
    ),
  });

  const isSupported =
    'MediaRecorder' in window && 'getUserMedia' in navigator.mediaDevices;

  // --- Duration timer helpers ---
  const startTimer = useCallback(() => {
    setRecordingDuration(0);
    recordingIntervalRef.current = setInterval(() => {
      setRecordingDuration((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  }, []);

  // --- Recording handlers ---
  const handleStartRecording = useCallback(async () => {
    try {
      const currentOutputLanguage = await getCurrentOutputLanguage();
      startTimer();

      if (USE_BATCH_MODE) {
        dispatch(startRecording());
        await batchRecorder.start();
      } else {
        dispatch(startStreamingRecording());
        dispatch(
          processStreamingAudioDictation({
            options: {
              mode,
              customVocabulary: teamCustomVocabulary,
              skipLLMProcessing: false,
              languageCode: navigator.language.split('-')[0],
              outputLanguage: currentOutputLanguage,
            },
          }),
        );
      }
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  }, [dispatch, mode, teamCustomVocabulary, startTimer, batchRecorder]);

  const handleStopRecording = useCallback(() => {
    if (USE_BATCH_MODE) {
      batchRecorder.stop();
    } else {
      dispatch(stopStreamingRecording());
    }
    stopTimer();
  }, [dispatch, stopTimer, batchRecorder]);

  // --- Microphone permission ---
  useEffect(() => {
    if (isModalOpen && isSupported) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          setIsPermissionGranted(true);
          stream.getTracks().forEach((track) => track.stop());
        })
        .catch((err) => {
          console.error('Microphone permission denied:', err);
          setIsPermissionGranted(false);
        });
    }
  }, [isModalOpen, isSupported]);

  // --- Alt/Option key hold-to-record ---
  useEffect(() => {
    if (!isModalOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'AltLeft' || event.code === 'AltRight') {
        if (!event.repeat && !isSpacePressed && isPermissionGranted) {
          event.preventDefault();
          setIsSpacePressed(true);

          if (status === 'idle' || status === 'success') {
            if (status === 'success') {
              dispatch(resetDictation());
              setTimeout(() => {
                handleStartRecording();
              }, 50);
            } else {
              handleStartRecording();
            }
          }
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (
        (event.code === 'AltLeft' || event.code === 'AltRight') &&
        isSpacePressed
      ) {
        event.preventDefault();
        setIsSpacePressed(false);
        if (status === 'recording') {
          handleStopRecording();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    isModalOpen,
    status,
    isPermissionGranted,
    isSpacePressed,
    handleStartRecording,
    handleStopRecording,
    dispatch,
  ]);

  // --- Auto-copy transcript to clipboard ---
  useEffect(() => {
    if (transcript !== undefined) {
      setEditableTranscript(transcript || '');

      if (transcript && transcript.trim() !== '') {
        navigator.clipboard
          .writeText(transcript)
          .then(() => {
            setShowCopiedMessage(true);
            setTimeout(() => {
              setShowCopiedMessage(false);
            }, 3000);
          })
          .catch((err) => {
            console.error('Failed to copy to clipboard:', err);
          });
      }
    }
  }, [transcript]);

  // --- Close / cleanup ---
  const handleClose = () => {
    if (status === 'recording') {
      if (USE_BATCH_MODE) {
        batchRecorder.cancel();
      } else {
        handleStopRecording();
      }
    }

    dispatch(closeDictationModal());
    dispatch(resetDictation());
    setIsSpacePressed(false);
    setEditableTranscript('');
    setShowCopiedMessage(false);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleModeChange = (newMode: DictationMode) => {
    if (newMode === 'best') {
      guardLimits('dictationBestMode', () => {
        dispatch(setDictationMode(newMode));
      });
    } else {
      dispatch(setDictationMode(newMode));
    }
  };

  const handleNewRecording = () => {
    dispatch(resetDictation());
    setTimeout(() => {
      handleStartRecording();
    }, 100);
  };

  if (!isSupported) {
    return (
      <Dialog open={isModalOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {t(
                'dictation.modal.browserNotSupported.title',
                'Browser Not Supported',
              )}
            </DialogTitle>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertDescription>
              {t(
                'dictation.modal.browserNotSupported.message',
                "Your browser doesn't support audio recording. Please use a modern browser like Chrome, Firefox, or Edge.",
              )}
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button onClick={handleClose}>{t('common.close', 'Close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isModalOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('dictation.modal.title', 'Dictation')}</DialogTitle>
        </DialogHeader>
        <MacAppPromotionBanner variant="dictation" />
        <div className="px-2">
          {/* Header with Mode Selection and History Link */}
          <div className="flex items-center gap-3">
            {!showHistory && (
              <Select
                value={mode}
                onValueChange={(value) =>
                  handleModeChange(value as DictationMode)
                }
              >
                <SelectTrigger className="w-40 h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fast" className="py-2">
                    {t('dictation.modal.modes.fast', 'Fast Mode')}
                  </SelectItem>
                  <SelectItem value="best" className="py-2">
                    {t('dictation.modal.modes.best', 'Best Mode')}
                  </SelectItem>
                  <SelectItem value="answer" className="py-2">
                    {t('dictation.modal.modes.answer', 'Answer Mode')}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}

            {!showHistory && <OutputLanguageSelect variant="dictation" />}

            {showHistory ? (
              <Button
                onClick={() => setShowHistory(false)}
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={() => setShowHistory(true)}
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground ml-auto"
              >
                {t('dictation.modal.history.title', 'History')}
              </Button>
            )}
          </div>

          {/* Permission Warning */}
          {!showHistory && !isPermissionGranted && (
            <Alert variant="warning" className="mt-2 mb-2">
              <AlertDescription>
                {t(
                  'dictation.modal.permissionWarning',
                  'Please allow microphone access to use dictation',
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Recording Area */}
          {!showHistory && (
            <div className="flex flex-col items-center py-4">
              {status === 'idle' && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={handleStartRecording}
                        disabled={!isPermissionGranted}
                        className="w-[120px] h-[120px] rounded-full bg-primary text-white hover:bg-primary/90 hover:scale-105 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        aria-label="Start recording"
                      >
                        <Mic size={40} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {t(
                          'dictation.modal.tooltips.clickToStart',
                          'Click to start recording',
                        )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {status === 'recording' && (
                <>
                  <button
                    type="button"
                    onClick={handleStopRecording}
                    className="w-[120px] h-[120px] rounded-full bg-destructive text-white hover:bg-destructive/90 animate-pulse flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    aria-label="Stop recording"
                  >
                    <Square size={30} className="fill-white" />
                  </button>
                  <p className="text-lg font-semibold mt-3">
                    {formatDuration(recordingDuration)}
                  </p>
                </>
              )}

              {status === 'processing' && (
                <Loader className="w-14 h-14 animate-spin text-primary" />
              )}

              {status === 'success' && (
                <div className="w-full">
                  <Textarea
                    rows={4}
                    value={editableTranscript}
                    onChange={(e) => setEditableTranscript(e.target.value)}
                    placeholder={
                      editableTranscript.trim() === ''
                        ? t('dictation.modal.nothingHeard', 'Nothing heard')
                        : ''
                    }
                    className="w-full mb-3 p-3 border bg-background resize-none placeholder:text-center placeholder:text-muted-foreground placeholder:text-lg"
                  />
                  {showCopiedMessage && (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <p className="text-sm font-medium text-green-600">
                        {t(
                          'dictation.modal.copiedToClipboard',
                          'Copied to Clipboard',
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {status === 'error' && (
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
                  <p className="text-destructive mt-4">
                    {error ||
                      t(
                        'dictation.modal.error.generic',
                        'An error occurred during dictation',
                      )}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* History Panel */}
          {showHistory && (
            <div className="min-h-[300px]">
              <DictationHistory
                onCopy={() => {
                  setShowCopiedMessage(true);
                  setTimeout(() => {
                    setShowCopiedMessage(false);
                  }, 3000);
                }}
              />
            </div>
          )}

          {/* Spacebar Indicator - Always at Bottom */}
          {!showHistory && (
            <div className="flex justify-center items-center gap-2 mt-4">
              <Badge
                variant={isSpacePressed ? 'default' : 'outline'}
                className={cn(
                  'px-6 py-1.5 transition-colors text-sm',
                  isSpacePressed
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground',
                )}
              >
                {status !== 'recording' && (
                  <Keyboard className="w-4 h-4 mr-2" />
                )}
                {status === 'recording'
                  ? t(
                      'dictation.modal.keyboard.releaseOption',
                      'Release Option to stop',
                    )
                  : t(
                      'dictation.modal.keyboard.holdOption',
                      'Hold Option to record',
                    )}
              </Badge>
            </div>
          )}
        </div>
        {/* Action Buttons */}
        {(status === 'success' || status === 'error') && (
          <DialogFooter>
            <Button onClick={handleNewRecording}>
              {status === 'success'
                ? t('dictation.modal.actions.newDictation', 'New Dictation')
                : t('dictation.modal.actions.tryAgain', 'Try Again')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DictationModal;
